/**
 * 绑定下游卡页面
 * 输入卡号(PAN)和安全码(CVV)，调用 bindDownstreamCardV2 接口
 * 成功后显示"已提交"页面
 */

import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
import FixedBottom from '@/components/FixedBottom'
import CardView from '../components/card-view'
import { APIWeb } from '@/api'

interface LocationState {
  /** 卡片背景图 */
  backgroundImage?: string
  /** 卡BIN（4位卡头） */
  cardBin?: string
  /** 持卡人KYC ID */
  kycId?: number
  /** 卡产品ID */
  productId?: number
  /** 原始产品数据 */
  product?: ModelsCardProduct
  /** 持卡人名（first name） */
  kycFirstName?: string
  /** 持卡人姓（last name） */
  kycLastName?: string
}

export default React.memo(function BindDownstreamCard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as LocationState | null

  const backgroundImage = locationState?.backgroundImage || (locationState?.product as any)?.image || '/images/card-v2/electronic-card/zise-card-1.png'
  const cardBin = locationState?.cardBin
  const kycId = locationState?.kycId
  const productId = locationState?.productId
  const kycFirstName = locationState?.kycFirstName?.trim() ?? ''
  const kycLastName = locationState?.kycLastName?.trim() ?? ''

  const boundKycDisplayName = useMemo(
    () => [kycLastName, kycFirstName].filter(Boolean).join(' ').trim() || '--',
    [kycLastName, kycFirstName]
  )

  const pendingAuditHint = useMemo(() => {
    const digits = (cardBin || '').replace(/\D/g, '')
    if (digits.startsWith('4938')) return t('正在审核持卡人信息预计1到10分钟')
    if (digits.startsWith('5258')) return t('正在审核持卡人信息预计24小时内')
    return t('我们的团队正在为您完成身份验证验证通过后您即可正常使用相关服务')
  }, [cardBin, t])

  // 表单状态
  const [cardNumber, setCardNumber] = useState('')
  const [cvv, setCvv] = useState('')
  const [loading, setLoading] = useState(false)

  // 是否已提交成功
  const [submitted, setSubmitted] = useState(false)

  // 表单验证
  const canSubmit = useMemo(() => {
    const cardDigits = cardNumber.replace(/\D/g, '')
    return cardDigits.length >= 13 && cvv.trim().length >= 3 && !!kycId && !!productId
  }, [cardNumber, cvv, kycId, productId])

  // 提交绑定
  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !kycId || !productId) return

    setLoading(true)
    try {
      const result = await APIWeb.bindDownstreamCardV2({
        pan: cardNumber.replace(/\D/g, ''),
        cvv: cvv.trim(),
        kyc_id: kycId,
        pay_type: 'USDT',
        product_id: productId,
        idempotency_key: uuidv4(),
      })

      // action === 1 时跳转KYC编辑页面
      if (result?.action === 1) {
        navigate('/my/cardholder/edit', {
          state: { kycId },
        })
        return
      }

      // 提交成功，显示成功页面
      setSubmitted(true)
    } catch (error: any) {
      console.error('绑定下游卡失败:', error)
    } finally {
      setLoading(false)
    }
  }, [canSubmit, cardNumber, cvv, kycId, productId])

  // 完成 - 返回卡片主页
  const handleFinish = useCallback(() => {
    navigate('/card', { replace: true })
  }, [navigate])

  // 已提交成功页面
  if (submitted) {
    return (
      <PageLayout isHeader={false}>
        <div className="flex-1 flex flex-col items-center justify-center px-5">
          <img src="/images/my/icons/submit-success.svg" alt="" />
          <div className="text-7 font-medium text-[#1D2129] my-4">
            {t('已提交')}
          </div>
          <div className="text-base text-[#1D2129] text-center px-2">
            {t('此卡已绑定', { name: boundKycDisplayName })}
          </div>
          <div className="text-sm font-normal leading-6 text-[#667085] text-center max-w-70 mt-2">
            {pendingAuditHint}
          </div>
        </div>

        <FixedBottom className="bg-white">
          <CustomButton onClick={handleFinish}>
            {t('完成')}
          </CustomButton>
        </FixedBottom>
      </PageLayout>
    )
  }

  // 绑定卡片表单页面
  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={
          <span className="text-base font-medium text-black">
            {t('绑定卡片')}
          </span>
        }
      />

      <div className="px-5 pt-6 pb-24">
        {/* 卡片预览 */}
        <CardView
          cardStatus="pending"
          backgroundImage={backgroundImage}
          cardBin={cardBin}
        />

        {/* 卡号 */}
        <div className="mt-6 mb-6">
          <div className="text-sm text-[#667085] font-medium mb-2">{t('卡号')}</div>
          <CustomTextInput
            value={cardNumber}
            onChangeText={(text) => setCardNumber(text.replace(/\D/g, '').slice(0, 19))}
            placeholder={t('输入您的卡号')}
            inputMode="numeric"
            maxLength={19}
          />
        </div>

        {/* 安全码 */}
        <div className="mb-6">
          <div className="text-sm text-[#667085] font-medium mb-2">{t('安全码')}</div>
          <CustomTextInput
            value={cvv}
            onChangeText={(text) => setCvv(text.replace(/\D/g, '').slice(0, 4))}
            placeholder="CVV"
            inputMode="numeric"
            maxLength={4}
          />
        </div>
      </div>

      <FixedBottom className="bg-white">
        <CustomButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={loading}
        >
          {t('立即绑定')}
        </CustomButton>
      </FixedBottom>
    </PageLayout>
  )
})

